import { VariantProps, cva } from 'class-variance-authority'
import { type ButtonHTMLAttributes, ReactNode } from 'react'
import LoadingSpinner from '../loading-spinner'
import classNames from 'classnames'

const buttonStyles = cva(
  [
    'outline-none font-semibold flex items-center justify-center transition-all gap-2 rounded text-[15px] border relative',
    'active:ring-2 focus-visible:ring-2 group',
  ],
  {
    variants: {
      size: {
        xs: 'p-1.5',
        sm: 'py-[7px] px-[12px]',
        md: 'py-[9.5px] px-[16px]',
        lg: 'py-[12px] px-[20px]',
      },
      variant: {
        primary: [
          'bg-primary-500 text-gray-950 border-primary-700',
          'hover:bg-primary-600 focus-visible:ring-primary-200 active:ring-primary-200 disabled:bg-primary-600',
        ],
        secondary: [
          'bg-white text-gray-900 border-gray-400',
          'hover:bg-gray-300 focus-visible:ring-primary-200 active:ring-primary-200 disabled:bg-gray-300',
        ],
        dark: [
          'bg-gray-900 text-white border-gray-900',
          'hover:bg-gray-700 focus-visible:ring-primary-200 active:ring-primary-200 disabled:bg-gray-700',
        ],
        'danger-outline': [
          'text-red-500 border-transparent text-medium !p-0',
        ],
        'danger': [
          'border-red-500 bg-red-500 text-white',
        ],
        outline: [
          'bg-transparent border border-black-500 text-black-900',
          'hover:bg-black-100 hover:border-black-100',
        ],
      },
      isBlock: { true: 'w-full' },
      isDisabled: { true: '' },
      isDanger: { true: '' },
    },

    defaultVariants: {
      variant: 'primary',
      size: 'md',
      isBlock: false,
      isDisabled: false,
    },
  },
)

type ButtonStyling = VariantProps<typeof buttonStyles>;

interface PropTypes extends ButtonHTMLAttributes<HTMLButtonElement>, ButtonStyling {
  isLoading?: boolean
  children: ReactNode
  toolTip?: string
}

export default function Button ({
  type = 'button',
  variant,
  size = 'md',
  children,
  isLoading = false,
  isDisabled = false,
  isBlock = false,
  className,
  toolTip,
  ...rest
}: PropTypes) {
  return (
    <button
      className={classNames(className, buttonStyles({ variant, isBlock, isDisabled, size }))}
      type={type}
      disabled={isDisabled || isLoading}
      {...rest}
    >
      {
        isLoading
        && <div className="absolute inset-0 flex items-center justify-center rounded">
          <LoadingSpinner className="w-[15px] h-[15px]" />
        </div>
      }

      <span className={isLoading ? 'invisible' : ''}>
        {children}
      </span>

      {
        toolTip
        && <div className="absolute invisible group-hover:visible bg-gray-700 -bottom-10 px-2 py-1 whitespace-nowrap text-sm rounded z-10">
          {toolTip}
        </div>
      }
    </button>
  )
}
